'use strict';

// external imports
import React, {
    useCallback,
    useMemo
} from 'react';
import {useSelector} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';
import {
    Dropdown,
    DropdownChangeEvent
} from 'primereact/dropdown';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';

// frontend imports
import {
    QueryType,
    schemata
} from '~/frontend/src/modules/panels/QueryExecute';
import {
    getToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

// action imports
import {StoreState} from '~/frontend/src/store';


export type DefaultProps = {
    showAskAiButton? : boolean;
    showExecuteButton? : boolean;
    showQueryTypeButtons? : boolean;
    executeButtonDisabled? : boolean;
    askAiButtonDisabled? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    showAskAiButton      : false,
    showExecuteButton    : true,
    showQueryTypeButtons : true,
    executeButtonDisabled: true,
    askAiButtonDisabled  : true
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    queryType : QueryType;
    schema : Schema;

    onAskAi : () => void;
    onExecute : () => void;
    onQueryTypeChange : ( type : QueryType ) => void;
    onSchemaChange : ( schema : Schema ) => void;
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const {
              askAiButtonDisabled,
              executeButtonDisabled,
              onAskAi,
              onExecute,
              onQueryTypeChange,
              onSchemaChange,
              queryType,
              schema,
              showAskAiButton,
              showExecuteButton,
              showQueryTypeButtons,
              uid
          }                       = props,
          user                    = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          language                = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          onExecuteButtonClick    = useCallback(
              () => {
                  onExecute();
              },
              [
                  onExecute
              ]
          ),
          onCypherButtonClick     = useCallback(
              () => {
                  onQueryTypeChange( 'Cypher' );
              },
              [
                  onQueryTypeChange
              ]
          ),
          onSqlButtonClick        = useCallback(
              () => {
                  onQueryTypeChange( 'SQL' );
              },
              [
                  onQueryTypeChange
              ]
          ),
          onSchemaDropdownChange  = useCallback(
              ( event : DropdownChangeEvent ) => {
                  if ( event.value ) {
                      const schemaToSet = ( event.value === 'CONFIG'
                          ? 'CFG'
                          : event.value ) as Schema;

                      onSchemaChange( schemaToSet );
                  }
              },
              [
                  onSchemaChange
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showQueryTypeButtons ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_panelButtons`,

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_cypherButton`,

                                    label    : __( 'YN.base.frontend.queryExecute.queryType.cypher.label' ),
                                    onClick  : onCypherButtonClick,
                                    className: queryType === 'Cypher' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_sqlButton`,

                                    label    : __( 'YN.base.frontend.queryExecute.queryType.sql.label' ),
                                    onClick  : onSqlButtonClick,
                                    className: queryType === 'SQL' ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            if ( queryType === 'Cypher' ) {
                layoutComponents.left.push( {
                    type: 'separator'
                } );

                layoutComponents.left.push( {
                    type: 'genericNode',

                    params: <div key={`${uid}_schema`} className="field">
                        <label
                            htmlFor={`${uid}_schemaDropdown_i`}
                            className="block"
                        >
                            {__( 'YN.base.frontend.queryExecute.input.schema.label' )}
                        </label>
                        <Dropdown
                            id={`${uid}_schemaDropdown`}
                            inputId={`${uid}_schemaDropdown_i`}
                            value={schema === 'CFG' ? 'CONFIG' : schema}
                            onChange={onSchemaDropdownChange}
                            options={schemata.map( s => s === 'CFG' ? 'CONFIG' : s )}
                            className="yn-inputgroup"
                            style={{ width: '7rem' }}
                        />
                    </div>
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showAskAiButton ) {
                layoutComponents.left.push( {
                    type  : 'button',
                    params: {
                        key: `${uid}_askAiButton`,

                        icon     : 'material-symbols-outlined ::chat_paste_go::',
                        className: 'p-button-text',
                        tooltip  : __( 'YN.base.frontend.queryExecute.button.askAi.tooltip' ),
                        onClick  : onAskAi,
                        disabled : askAiButtonDisabled
                    }
                } );
            }

            if ( showExecuteButton ) {
                layoutComponents.left.push( {
                    type  : 'button',
                    params: {
                        key: `${uid}_executeButton`,

                        icon     : 'material-symbols-outlined ::order_play::',
                        className: 'p-button-text',
                        tooltip  : __( 'YN.base.execute.button' ),
                        onClick  : onExecuteButtonClick,
                        disabled : executeButtonDisabled
                    }
                } );
            }

            // right

            const toolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                left={toolbarComponents.left}
                right={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />
        },
        [
            language,
            messageCatalogTimestamp,
            onCypherButtonClick,
            onExecuteButtonClick,
            onSchemaDropdownChange,
            onSqlButtonClick,
            queryType,
            schema,
            showExecuteButton,
            showQueryTypeButtons,
            uid
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'XmlImport-Input-Toolbar';

export default Toolbar;
